import { getAppCopy, isPurchaseCancelledMessage } from '../../../i18n';
import type { EntitlementStatus } from '../types';
import { PurchaseCancelledError } from './appStore';

type WindowsStoreBridge = {
  isAvailable: () => Promise<boolean>;
  getEntitlement: () => Promise<{
    tier?: 'free' | 'pro';
    source?: EntitlementStatus['source'];
    formattedPrice?: string;
    storeSubscriptionId?: string;
  }>;
  purchase: () => Promise<{
    tier?: 'free' | 'pro';
    source?: EntitlementStatus['source'];
    formattedPrice?: string;
  }>;
  restore: () => Promise<boolean>;
  manageSubscriptions: () => Promise<void>;
  getSubscriptionForRedeem: () => Promise<{
    storeSubscriptionId: string;
    userCollectionId?: string;
  } | null>;
};

function getWindowsStore(): WindowsStoreBridge | null {
  const store = (window as { electron?: { store?: WindowsStoreBridge } }).electron?.store;
  return store ?? null;
}

export function isWindowsStorePurchasePlatform(): boolean {
  return Boolean(getWindowsStore());
}

function mapEntitlement(data: {
  tier?: 'free' | 'pro';
  source?: EntitlementStatus['source'];
  formattedPrice?: string;
}): EntitlementStatus {
  return {
    tier: data.tier === 'pro' ? 'pro' : 'free',
    source: 'microsoft',
    formattedPrice: data.formattedPrice,
  };
}

export async function fetchWindowsStoreEntitlement(): Promise<EntitlementStatus> {
  const store = getWindowsStore();
  if (!store) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  const available = await store.isAvailable();
  if (!available) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  return mapEntitlement(await store.getEntitlement());
}

export async function purchaseWindowsStore(): Promise<void> {
  const store = getWindowsStore();
  if (!store) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
  try {
    const result = await store.purchase();
    if (result.tier !== 'pro') {
      throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    const code = (error as { code?: string }).code;
    if (code === 'cancelled' || message === 'cancelled' || isPurchaseCancelledMessage(message)) {
      throw new PurchaseCancelledError();
    }
    throw error;
  }
}

export async function restoreWindowsStore(): Promise<boolean> {
  const store = getWindowsStore();
  if (!store) return false;
  return store.restore();
}

export async function manageWindowsStoreSubscription(): Promise<void> {
  const store = getWindowsStore();
  if (!store) {
    throw new Error(getAppCopy().billingErrors.manageUnavailable);
  }
  await store.manageSubscriptions();
}

export async function getWindowsStoreSubscriptionForRedeem(): Promise<{
  storeSubscriptionId: string;
  userCollectionId?: string;
} | null> {
  const store = getWindowsStore();
  if (!store) return null;
  return store.getSubscriptionForRedeem();
}
